import flatten from 'lodash/flatten';
import orderBy from 'lodash/orderBy';

import {SankeyNode} from './sankey-node';
import SankeyEdge from './sankey-edge';
import {SankeyNodeType, SankeyNodeTypePriority} from './types';
import {SankeyStep} from './sankey-step';
import {SankeyBreakdown} from './sanky-breakdown';
import {SerializedSankeyFlow, Metadata} from './serialized';

export default class SankeyFlow {
  public breakdowns: SankeyBreakdown[];
  public computedAt: string;
  public metadata: Metadata;
  public steps: SankeyStep[];

  constructor({breakdowns, computedAt, computed_at, metadata, steps}: SerializedSankeyFlow) {
    this.breakdowns = (breakdowns || []).map((b) => new SankeyBreakdown(b));
    this.computedAt = computedAt || computed_at; // eslint-disable-line @typescript-eslint/camelcase
    this.metadata = metadata && {
      minSamplingFactor: metadata.minSamplingFactor || metadata[`min_sampling_factor`],
    }; // TODO: remove after funnels migration

    this.steps = steps
      .filter((step) => step && step.nodes)
      .map(
        (step, stepNumber) =>
          new SankeyStep({
            ...step,
            step: stepNumber,
          }),
      );

    // fill edge information for all nodes in the flow
    this.fillFlowEdges();
  }

  /**
   * Returns the first step in the flow considered an anchor step
   */
  public getFirstAnchorStep() {
    return this.steps.find((step) => step.nodes.some((node) => node.type === SankeyNodeType.Anchor));
  }

  /**
   * Calculates the top path for incoming and outgoing
   * edges for a given start node, represented as a
   * set of edge ids
   */
  public calculateTopFlowPath(startNode): object {
    const path = new Set();

    // add incoming edges if startNode type is a drop-off node
    // otherwise add all outgoing edges
    if (startNode.type === SankeyNodeType.Dropoff) {
      startNode.incomingEdges.forEach((edge) => path.add(edge.edgeId));
    } else {
      startNode.edges.forEach((edge) => path.add(edge.edgeId));
    }

    // highlight incoming and outgoing top paths
    this.findNodeTopEdges(path, startNode, `from`);
    this.findNodeTopEdges(path, startNode, `to`);

    return path;
  }

  private findNodeTopEdges(path, node, direction): void {
    const edgesPropertyName = direction === `from` ? `incomingEdges` : `edges`;
    const nodePropertyName = direction === `from` ? `fromNode` : `toNode`;
    const topEdge = this.findTopEdge(node[edgesPropertyName], nodePropertyName);

    if (topEdge) {
      let currentNode = topEdge[nodePropertyName];
      path.add(topEdge.edgeId);

      while (currentNode) {
        const currentNodeTopEdge = this.findTopEdge(currentNode[edgesPropertyName], nodePropertyName);

        if (!currentNodeTopEdge) {
          break;
        }

        path.add(currentNodeTopEdge.edgeId);
        currentNode = currentNodeTopEdge[nodePropertyName];
      }
    }
  }

  private findTopEdge(edges, nodePropertyName): SankeyEdge {
    const sortedEdges = orderBy(
      edges,
      [
        // consider event to event edges first
        (edge) => SankeyNodeTypePriority[edge[nodePropertyName].type],
        // consider edges with greatest weight first
        `totalCount`,
      ],
      [`desc`, `desc`],
    );

    return sortedEdges.length ? sortedEdges[0] : null;
  }

  private findNode(stepIndex, id): SankeyNode {
    const step = this.steps[stepIndex];
    return step.nodes.find((node) => node.id === id);
  }

  private fillFlowEdges() {
    const nodes = flatten(this.steps.map((step) => step.nodes));

    for (const node of nodes) {
      for (const edge of node.edges) {
        const targetNode = this.findNode(edge.step, edge.id);
        edge.fromNode = node;
        edge.toNode = targetNode;

        targetNode.addIncomingEdge(edge);
      }

      node.sortEdges();
    }
  }
}
